import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button/Button';
import Icon from '@material-ui/core/Icon/Icon';
import Grid from '@material-ui/core/Grid/Grid';
import Typography from '@material-ui/core/Typography';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth/withWidth';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import AnalyticsWrapper from '@configuratorware/configurator-frontendgui/App/Shared/Components/Analytics/AnalyticsWrapper';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import PreviewPortal from '../PreviewPortal';
import Thumbnail from '../../Containers/Thumbnail';
import { FullscreenIcon } from '../../../../../Icons';

const styles = (theme) => ({
  viewsContainer: {
    position: 'absolute',
    bottom: 0,
  },
  fullScreenWrapper: {
    display: 'flex;',
    justifyContent: 'center',
  },
  button: {
    '& > span': {
      marginTop: 0,
      [theme.breakpoints.up('sm')]: {
        marginTop: 2,
      },
    },
    borderRadius: 100,
    textTransform: 'none',
    zIndex: 2,
    minWidth: 0,
    maxHeight: 38,
    paddingLeft: 12,
    paddingRight: 12,
    '&:hover': {
      background: '#F2F2F2',
    },
    ' &:active, &:focus': {
      background: '#C9C9C9',
    },
  },
  buttonBottom: {},
  buttonTop: {},
  buttonIcon: {
    width: 30,
    height: 30,
    paddingLeft: theme.layout.buttonIcon.paddingLeft,
    position: 'relative',
    top: 0,
    [theme.breakpoints.up('sm')]: {
      marginTop: -1,
    },
  },
  thumbnailGrid: {
    [theme.breakpoints.between('xs', 'md')]: {
      width: 'calc(100% - 360px)', // subtract with right toolbar width
      maxWidth: 'calc(100% - 360px)',
      marginBottom: 0,
    },
    // align it with the main container, so it will be aligned with the Designer image
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      bottom: 0,
      left: 'calc(50% - 175px)', // subtract half of the right sidebar width
      width: 453, // 2 x 24 (arrow width) + 425 (carousel width)
      // transform: 'translateX(-50%)',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: '100%',
      bottom: '-40px',
    },
  },
  buttonView: {
    textTransform: 'none',
    bottom: 35,
    position: 'absolute',
    left: 'calc(50% - 200px)',
    [theme.breakpoints.down('xs')]: {
      left: 'calc(50% - 75px)',
      bottom: 0,
      height: 56,
    },
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailComponent: {
    width: 'auto',
    padding: '0px 18px',
    minWidth: 180,
  },
  previewText: {
    marginTop: 1,
    fontWeight: 'bold',
    lineHeight: `${12}px`,
    fontSize: 14,
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      lineHeight: `${14}px`,
    },
    [theme.breakpoints.up('xl')]: {
      lineHeight: `${14}px`,
    },
  },
});

export const PreviewButton = ({
  classes, bottom, top, onClick, showTypography,
}) => (
  <AnalyticsWrapper targetId="preview_open">
    <Button
      disableRipple
      component="div"
      className={clsx(
        classes.button,
      )}
      onClick={onClick}
    >
      <Typography variant="body2" className={classes.previewText}>
        {showTypography ? t('configurator.previewButton') : t('configurator.previewLabel')}
      </Typography>
      <FullscreenIcon className={classes.buttonIcon}>
        {/*
        {showTypography ? 'fullscreen' : 'remove_red_eye'}
        */}
      </FullscreenIcon>
    </Button>
  </AnalyticsWrapper>
);
PreviewButton.propTypes = {
  classes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  bottom: PropTypes.bool,
  top: PropTypes.bool,
  showTypography: PropTypes.bool,
};

class FullScreenView extends React.Component {
    static propTypes = {
      handleOpenPreview: PropTypes.func,
      handleClosePreview: PropTypes.func,
      hasItems: PropTypes.bool,
      classes: PropTypes.object,
      children: PropTypes.any,
      showPreviewButton: PropTypes.bool,
      showDesignViewList: PropTypes.bool,
      width: PropTypes.string,
      dialogStyle: PropTypes.string,
      areAllDesignAreasEmpty: PropTypes.bool,
      renderTopPreviewButton: PropTypes.func,
      ThumbnailComponent: PropTypes.elementType,
      thumbnailProps: PropTypes.object,
      thumbnailDotMode: PropTypes.bool,
      renderButtonOnTop: PropTypes.bool,
    };

    static defaultProps = {
      showPreviewButton: true,
      renderTopPreviewButton: ({ width, areAllDesignAreasEmpty, ...props }) => !isWidthUp('sm', width) && !areAllDesignAreasEmpty && <PreviewButton showTypography={isWidthUp('sm', width)} {...props} top />,
      ThumbnailComponent: Thumbnail,
      renderButtonOnTop: false,
    };

    state = {
      previewOpen: false,
    };

    openPreview = () => {
      this.setState({ previewOpen: true }, () => this.props.handleOpenPreview());
    };

    closePreview = () => {
      this.setState({ previewOpen: false }, () => this.props.handleClosePreview());
    };

    render() {
      const { previewOpen } = this.state;
      const {
        hasItems,
        classes,
        children,
        showPreviewButton,
        showDesignViewList,
        width,
        dialogStyle,
        renderTopPreviewButton,
        ThumbnailComponent,
        thumbnailProps,
        thumbnailDotMode,
        renderButtonOnTop,
      } = this.props;
      return previewOpen ? (
        <div className={classes.fullScreenWrapper}>
          <PreviewPortal
            closePreviewMode={this.closePreview}
            thumbnailClick={this.handleViewChange}
            previewOpen={previewOpen}
            hasItems={hasItems}
            dialogStyle={dialogStyle}
          >
            {children}
          </PreviewPortal>
        </div>
      ) : (
        <>
          {children}
          <Grid
            container
            direction="column"
            justify="space-between"
            className={clsx(classes.viewsContainer, customClassName('views'))}
          >
            {showDesignViewList ? (
              <>
                {showPreviewButton && (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8}
                  className={clsx(
                    classes.thumbnailGrid,
                    thumbnailDotMode, 'dots-container',
                  )}
                >
                  <div className={classes.previewContainer}>
                    {isWidthUp('sm', width)
                   && (
                   <>
                     <Typography variant="body2" className={classes.previewText}>{t('fullscreenView.moreViews')}</Typography>
                     <div className={classes.thumbnailComponent}>
                       <ThumbnailComponent {...thumbnailProps} />
                     </div>
                   </>
                   )}

                    <PreviewButton showTypography={isWidthUp('sm', width)} {...this.props} bottom onClick={this.openPreview} />
                  </div>
                </Grid>
                )}

              </>
            ) : (
              <>
                {showPreviewButton
                    && width !== 'xs' && (
                      <Grid
                        item
                        className={clsx(
                          classes.thumbnailGrid,
                        )}
                      >
                        <div className={classes.previewContainer}>
                          {isWidthUp('sm', width)
                       && (
                       <>
                         <Typography variant="body2" className={classes.previewText}>{t('fullscreenView.moreViews')}</Typography>
                         <div className={classes.thumbnailComponent}>
                           <ThumbnailComponent {...thumbnailProps} />
                         </div>
                       </>
                       )}

                          <PreviewButton showTypography={isWidthUp('sm', width)} {...this.props} bottom onClick={this.openPreview} />
                        </div>
                      </Grid>
                )}
              </>
            )}
          </Grid>
        </>
      );
    }
}

export default withWidth({ initialWidth: 'lg' })(
  withStyles(styles, { name: 'FullScreenView' })(FullScreenView),
);
